/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CareerSiteLocationResponse } from '../models/CareerSiteLocationResponse';
import type { CareerSitePositionResponse } from '../models/CareerSitePositionResponse';
import type { HireStagesResponse } from '../models/HireStagesResponse';
import type { HiringOpeningsResponse } from '../models/HiringOpeningsResponse';
import type { HistoricalConversionDataResponse } from '../models/HistoricalConversionDataResponse';
import type { OpeningDetailResponse } from '../models/OpeningDetailResponse';
import type { OverlappingSourcingPurchaseResponse } from '../models/OverlappingSourcingPurchaseResponse';
import type { Pagination } from '../models/Pagination';
import type { ReachCampaignResponse } from '../models/ReachCampaignResponse';
import type { SourcingAggregateSpendResponse } from '../models/SourcingAggregateSpendResponse';
import type { SourcingAnalytics } from '../models/SourcingAnalytics';
import type { SourcingBrandsResponse } from '../models/SourcingBrandsResponse';
import type { SourcingChannelDetails } from '../models/SourcingChannelDetails';
import type { SourcingChannels } from '../models/SourcingChannels';
import type { SourcingOpeningChannelsResponse } from '../models/SourcingOpeningChannelsResponse';
import type { SourcingOpeningDetailsResponse } from '../models/SourcingOpeningDetailsResponse';
import type { SourcingOpenings } from '../models/SourcingOpenings';
import type { SourcingOpeningsPayload } from '../models/SourcingOpeningsPayload';
import type { SourcingOpeningsResponse } from '../models/SourcingOpeningsResponse';
import type { SourcingOpeningUpdateParameters } from '../models/SourcingOpeningUpdateParameters';
import type { SourcingPurchaseBulkCreateRequest } from '../models/SourcingPurchaseBulkCreateRequest';
import type { SourcingPurchaseBulkCreateResponse } from '../models/SourcingPurchaseBulkCreateResponse';
import type { SourcingPurchaseDetails } from '../models/SourcingPurchaseDetails';
import type { SourcingPurchaseParameters } from '../models/SourcingPurchaseParameters';
import type { SourcingSnapshots } from '../models/SourcingSnapshots';
import type { SourcingStageStatsResponse } from '../models/SourcingStageStatsResponse';
import type { VonqContract } from '../models/VonqContract';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SourcingService {

    /**
     * Requests sourcing brands
     * Return sourcing brands for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param query Filter brands returned by matching address
     * @param isSelectedForSourcing Filter brands returned by matching is_selected_for_sourcing
     * @returns SourcingBrandsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingBrands(
        page?: number,
        perPage?: number,
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<SourcingBrandsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/brands',
            query: {
                'page': page,
                'per_page': perPage,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Requests sourcing openings
     * Return sourcing opening for the logged in user
     * @param selectedForSourcing Filter openings by is_selected_for_sourcing
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter openings returned by matching opening/location/position name
     * @param positionIds Filter openings returned by matching position_ids
     * @param locationIds Filter openings returned by matching location_ids
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param brandIds Filter openings returned by matching brand_ids
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardOpenings(
        selectedForSourcing?: boolean,
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        positionIds?: Array<string>,
        locationIds?: Array<string>,
        showAggregateData?: boolean,
        brandIds?: Array<string>,
    ): CancelablePromise<{
        data: Array<SourcingOpenings>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/openings',
            query: {
                'selected_for_sourcing': selectedForSourcing,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'position_ids[]': positionIds,
                'location_ids[]': locationIds,
                'show_aggregate_data': showAggregateData,
                'brand_ids[]': brandIds,
            },
        });
    }

    /**
     * Sourcing analytics
     * Return sourcing analytics for a date range
     * @param fromDate Start date
     * @param toDate End date
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardAnalytics(
        fromDate: string,
        toDate: string,
    ): CancelablePromise<{
        analytics: SourcingAnalytics;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/analytics',
            query: {
                'from_date': fromDate,
                'to_date': toDate,
            },
        });
    }

    /**
     * Sourcing snapshots
     * Return sourcing snapshots
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardSnapshots(
        showAggregateData?: boolean,
    ): CancelablePromise<{
        snapshots: SourcingSnapshots;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/snapshots',
            query: {
                'show_aggregate_data': showAggregateData,
            },
        });
    }

    /**
     * Update openings for sourcing
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiSourcingOpeningsMarkForSourcing(
        requestBody: SourcingOpeningsPayload,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/openings/mark_for_sourcing',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Update Hiring Goal Attributes
     * @param openingSlug Opening Slug
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingDashboard(
        openingSlug: string,
        requestBody: {
            budget_allotted?: number;
            expected_conversion?: number;
            expected_cpa?: number;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/dashboard/{opening_slug}',
            path: {
                'opening_slug': openingSlug,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `unprocessable entity`,
            },
        });
    }

    /**
     * Requests sourcing locations
     * Return sourcing locations for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter locations returned by matching address
     * @param isSelectedForSourcing Is added to sourcing dashboard
     * @returns CareerSiteLocationResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingLocations(
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<CareerSiteLocationResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/locations',
            query: {
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Requests hiring goal and sourcing details for an opening
     * Return sourcing opening details
     * @param openingSlug slug of the opening
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns SourcingOpeningDetailsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpenings(
        openingSlug: string,
        showAggregateData?: boolean,
    ): CancelablePromise<SourcingOpeningDetailsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'show_aggregate_data': showAggregateData,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Update Opening
     * @param openingExternalId external_id of the opening
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static putInternalApiSourcingOpenings(
        openingExternalId: string,
        requestBody: SourcingOpeningUpdateParameters,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/sourcing/openings/{opening_external_id}',
            path: {
                'opening_external_id': openingExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `job not found`,
            },
        });
    }

    /**
     * Requests sourcing channels for an opening
     * Return sourcing opening channels details
     * @param openingSlug slug of the opening
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns SourcingOpeningChannelsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug: string,
        showAggregateData?: boolean,
    ): CancelablePromise<SourcingOpeningChannelsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_channels',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'show_aggregate_data': showAggregateData,
            },
        });
    }

    /**
     * Requests stats for sourcing stages for an opening
     * Return sourcing stage stats
     * @param openingSlug slug of the opening
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns SourcingStageStatsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingStageStats(
        openingSlug: string,
        showAggregateData?: boolean,
    ): CancelablePromise<SourcingStageStatsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_stage_stats',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'show_aggregate_data': showAggregateData,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Requests sourcing channels as recommendation
     * Return recommended sourcing channels list
     * @param openingSlug slug of the opening
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsGeneralRecommendations(
        openingSlug: string,
    ): CancelablePromise<{
        recommended_channels: Array<SourcingChannelDetails>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/general_recommendations',
            path: {
                'opening_slug': openingSlug,
            },
        });
    }

    /**
     * Maps sourcing stages to hire stages
     * @param openingSlug slug of the opening
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingOpeningsMapSourcingStages(
        openingSlug: string,
        requestBody: {
            stages: Array<{
                stage_type: 'landing' | 'assess' | 'screening' | 'interview' | 'offer' | 'hired' | 'custom';
                title: string;
                stage_id: string;
                expected_conversion: number | null;
                is_automated: boolean;
            }>;
        },
    ): CancelablePromise<{
        message: string;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/openings/{opening_slug}/map_sourcing_stages',
            path: {
                'opening_slug': openingSlug,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `opening not found`,
                422: `validation errors`,
            },
        });
    }

    /**
     * Requests hire stages for a given opening
     * Return stages with the respective conversion data
     * @param openingSlug slug of the opening
     * @returns HireStagesResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHireStages(
        openingSlug: string,
    ): CancelablePromise<HireStagesResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/hire_stages',
            path: {
                'opening_slug': openingSlug,
            },
        });
    }

    /**
     * Requests historical conversion data
     * Return historical conversion data
     * @param openingSlug slug of the opening
     * @param channel channel slug
     * @returns HistoricalConversionDataResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHistoricalConversionData(
        openingSlug: string,
        channel?: string,
    ): CancelablePromise<HistoricalConversionDataResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/historical_conversion_data',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'channel': channel,
            },
        });
    }

    /**
     * Requests sourcing funnels for an opening
     * Return sourcing funnels details
     * @param openingSlug slug of the opening
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns SourcingOpeningsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingFunnels(
        openingSlug: string,
        showAggregateData?: boolean,
    ): CancelablePromise<SourcingOpeningsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_funnels',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'show_aggregate_data': showAggregateData,
            },
        });
    }

    /**
     * Requests aggregate spend of sourcing funnels for an opening
     * Return aggregate spend of sourcing funnels
     * @param openingSlug slug of the opening
     * @returns SourcingAggregateSpendResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsAggregateSpend(
        openingSlug: string,
    ): CancelablePromise<SourcingAggregateSpendResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/aggregate_spend',
            path: {
                'opening_slug': openingSlug,
            },
        });
    }

    /**
     * Requests hiring openings for a given opening
     * Return hiring openings with hire counts.
     * @param openingSlug slug of the opening
     * @returns HiringOpeningsResponse successful with hiring openings
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug: string,
    ): CancelablePromise<HiringOpeningsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/hiring_openings',
            path: {
                'opening_slug': openingSlug,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Request for Opening Details
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiSourcingOpeningsOpeningsByExternalIds(
        requestBody: {
            opening_external_ids: Array<string>;
        },
    ): CancelablePromise<{
        openings: Array<OpeningDetailResponse>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/openings/openings_by_external_ids',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Requests sourcing positions
     * Return sourcing positions for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter positions returned by matching name
     * @param isSelectedForSourcing Is added to sourcing dashboard
     * @returns CareerSitePositionResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingPositions(
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<CareerSitePositionResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/positions',
            query: {
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Requests sourcing channels
     * Return sourcing channels of the account
     * @returns SourcingChannels successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingChannels(): CancelablePromise<SourcingChannels> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_channels',
        });
    }

    /**
     * Update Sourcing Campaigns
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesUpdateCampaignStatus(
        sourcingPurchaseId: string,
        requestBody: {
            enabled: boolean | null;
            channel: string | null;
            reach_campaign_id: string | null;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/update_campaign_status',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Update Sourcing Purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static putInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId: string,
        requestBody: SourcingPurchaseParameters,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `sourcing purchase not found`,
                422: `validation errors`,
            },
        });
    }

    /**
     * Requests sourcing purchase and its campaign details
     * Return sourcing purchase details
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @returns SourcingPurchaseDetails successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId: string,
    ): CancelablePromise<SourcingPurchaseDetails> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            errors: {
                404: `sourcing purchase not found`,
            },
        });
    }

    /**
     * Accept Sourcing Purchase Recommendation
     * Accept a budget recommendation for a sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesAcceptRecommendation(
        sourcingPurchaseId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/accept_recommendation',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            errors: {
                404: `sourcing purchase not found`,
                422: `unable to accept recommendation`,
            },
        });
    }

    /**
     * Reject Sourcing Purchase Recommendation
     * Reject a budget recommendation for a sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesRejectRecommendation(
        sourcingPurchaseId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/reject_recommendation',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            errors: {
                422: `unable to reject recommendation`,
            },
        });
    }

    /**
     * Get Sourcing Purchase Campaign Details
     * Returns campaign details for a sourcing purchase. Supported only for reach sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param showAggregateData To show aggregate data or funnel data in the result
     * @returns ReachCampaignResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingPurchasesCampaignDetails(
        sourcingPurchaseId: string,
        showAggregateData: boolean,
    ): CancelablePromise<ReachCampaignResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/campaign_details',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            query: {
                'show_aggregate_data': showAggregateData,
            },
            errors: {
                404: `sourcing purchase not found`,
            },
        });
    }

    /**
     * Validate Sourcing Purchases
     * @param requestBody
     * @returns OverlappingSourcingPurchaseResponse successful
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingPurchasesValidateSourcingPurchases(
        requestBody: {
            channel_slug: string;
            start_date: string;
            end_date: string;
            opening_ids: Array<string>;
        },
    ): CancelablePromise<OverlappingSourcingPurchaseResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_purchases/validate_sourcing_purchases',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Bulk Create Sourcing Purchases
     * Creates multiple sourcing purchases in a single transaction
     * @param requestBody
     * @returns SourcingPurchaseBulkCreateResponse successfully created
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody: SourcingPurchaseBulkCreateRequest,
    ): CancelablePromise<SourcingPurchaseBulkCreateResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_purchases/bulk_create',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * Creates a Vonq contract
     * @param requestBody
     * @returns VonqContract Vonq contract created
     * @throws ApiError
     */
    public static postInternalApiSourcingVonqContracts(
        requestBody: VonqContract,
    ): CancelablePromise<VonqContract> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/vonq_contracts',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Unprocessable entity`,
            },
        });
    }

    /**
     * Deletes a Vonq contract
     * @param vonqContractId ID of the Vonq contract to delete
     * @returns any Contract successfully deleted
     * @throws ApiError
     */
    public static deleteInternalApiSourcingVonqContracts(
        vonqContractId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/sourcing/vonq_contracts/{vonq_contract_id}',
            path: {
                'vonq_contract_id': vonqContractId,
            },
            errors: {
                404: `Not found`,
            },
        });
    }

}